import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Search,
  Trash2,
  Star,
  StarOff,
  Shield,
  Mic,
  FileText,
  Lightbulb,
  Mail,
  Play,
  Pause,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tsws_admin_token";

const authHeaders = (token) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [verifying, setVerifying] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [authed, setAuthed] = useState(false);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    axios
      .get(`${API}/admin/verify`, authHeaders(token))
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (verifying) {
    return (
      <div className="px-6 lg:px-12 py-24 text-center text-[var(--tsws-green)]">
        <p className="font-tag text-2xl">Checking session...</p>
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onSuccess={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); setAuthed(true); }} />;
  }

  return <AdminDashboard token={token} onLogout={logout} />;
}

function AdminLogin({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      toast.success("Welcome back, Sav.");
      onSuccess(data.token);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Login failed.";
      setError(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="px-6 lg:px-12 py-20 lg:py-28 text-[var(--tsws-green)] min-h-[70vh] flex items-center justify-center"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-8 lg:p-10 shadow-brutal"
      >
        <div className="flex items-center gap-3 mb-6">
          <Shield className="text-[var(--tsws-cigar)]" size={28} />
          <span className="font-label text-xs text-[var(--tsws-cigar)]">
            ⏤ Admin Area
          </span>
        </div>
        <h1 className="font-display text-5xl leading-none">
          private<span className="text-[var(--tsws-cigar)]">.</span>
        </h1>
        <p className="font-tag text-xl mt-2">Sav only.</p>

        <label className="block mt-8">
          <span className="block font-label text-xs mb-2 opacity-80">Password</span>
          <div className="relative">
            <Lock
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tsws-green)]/60"
            />
            <input
              data-testid="admin-password"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="brutal-input pl-10"
              placeholder="••••••••"
            />
          </div>
        </label>

        {error && (
          <div
            data-testid="admin-login-error"
            className="mt-5 border-2 border-[var(--tsws-cigar)] bg-[var(--tsws-orange-light)] p-4 flex gap-2 items-start"
          >
            <AlertCircle className="text-[var(--tsws-cigar)] mt-0.5 shrink-0" size={18} />
            <p>{error}</p>
          </div>
        )}

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={submitting || !password}
          className="mt-7 w-full font-display text-lg px-6 py-4 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] disabled:opacity-60"
        >
          {submitting ? "checking..." : "let me in →"}
        </button>

      <style>{`
        .brutal-input {
          width: 100%;
          background: var(--tsws-orange);
          color: var(--tsws-green);
          border: 2px solid var(--tsws-green);
          padding: 12px 14px;
          font-family: 'Fraunces', Georgia, serif;
          font-size: 16px;
          outline: none;
          transition: background 200ms ease, color 200ms ease, box-shadow 200ms ease;
        }
        .brutal-input::placeholder { color: var(--tsws-green); opacity: 0.45; }
        .brutal-input:focus {
          background: var(--tsws-green);
          color: var(--tsws-orange);
          box-shadow: 4px 4px 0 0 var(--tsws-cigar);
        }
      `}</style>
      </form>
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("applications");

  return (
    <div data-testid="admin-dashboard" className="px-6 lg:px-12 py-12 lg:py-16 text-[var(--tsws-green)]">
      <div className="flex flex-wrap justify-between items-end gap-6 mb-10">
        <div>
          <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Sav HQ</span>
          <h1 className="font-display text-6xl lg:text-7xl mt-2 leading-none">
            command<br />deck<span className="text-[var(--tsws-cigar)]">.</span>
          </h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={onLogout}
          className="font-display text-base px-4 py-2 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] inline-flex items-center gap-2"
        >
          <LogOut size={16} /> logout
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b-2 border-[var(--tsws-green)] pb-3 mb-10">
        {[
          { id: "applications", label: "applications", icon: FileText, testid: "tab-applications" },
          { id: "voicemails", label: "voicemails", icon: Mic, testid: "tab-voicemails" },
          { id: "suggestions", label: "ideas", icon: Lightbulb, testid: "tab-suggestions" },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`font-display text-base px-4 py-2 border-2 inline-flex items-center gap-2 transition-colors ${
                active
                  ? "bg-[var(--tsws-green)] text-[var(--tsws-orange)] border-[var(--tsws-green)]"
                  : "border-[var(--tsws-green)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)]/10"
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "applications" && <ApplicationsPanel token={token} />}
      {tab === "voicemails" && <VoicemailsPanel token={token} />}
      {tab === "suggestions" && <SuggestionsPanel token={token} />}
    </div>
  );
}

// ===== Applications =====
function ApplicationsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/applications`, authHeaders(token))
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Couldn't load applications"))
      .finally(() => setLoading(false));
  }, [token]);

  const remove = async (id, e) => {
    e?.stopPropagation?.();
    if (!confirm("Delete this application forever?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, authHeaders(token));
      setItems((prev) => prev.filter((a) => a.id !== id));
      if (open === id) setOpen(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((a) =>
      [a.name, a.email, a.social_handle, a.location, a.topics, a.why_you, a.extra]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [items, q]);

  return (
    <div data-testid="applications-panel">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tsws-green)]/60" />
          <input
            data-testid="search-applications"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, topic..."
            className="brutal-input pl-10"
          />
        </div>
        <span className="font-label text-xs opacity-70">
          {filtered.length} of {items.length}
        </span>
      </div>

      {loading && <p className="opacity-60">Loading...</p>}
      {!loading && filtered.length === 0 && (
        <div className="border-2 border-dashed border-[var(--tsws-green)] p-10 text-center">
          <Mail className="mx-auto text-[var(--tsws-cigar)]" size={32} />
          <p className="font-display text-2xl mt-3">no applications yet.</p>
        </div>
      )}

      <ul className="space-y-3" data-testid="applications-list">
        {filtered.map((a) => (
          <li
            key={a.id}
            data-testid={`application-${a.id}`}
            className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-5"
          >
            <button
              onClick={() => setOpen(open === a.id ? null : a.id)}
              className="w-full text-left flex flex-wrap items-baseline gap-x-4 gap-y-1"
            >
              <span className="font-display text-2xl">{a.name.toLowerCase()}</span>
              <span className="font-tag text-base opacity-80">{a.email}</span>
              {a.location && <span className="text-sm opacity-70">📍 {a.location}</span>}
              <span className="text-xs opacity-50 ml-auto">
                {new Date(a.submitted_at).toLocaleString()}
              </span>
            </button>
            {open === a.id && (
              <div className="mt-4 pt-4 border-t-2 border-[var(--tsws-green)]/30 space-y-3">
                {a.social_handle && (
                  <p><strong>Social:</strong> {a.social_handle}</p>
                )}
                <p><strong>Topics:</strong><br /><span className="opacity-90">{a.topics}</span></p>
                <p><strong>Why them:</strong><br /><span className="opacity-90">{a.why_you}</span></p>
                {a.extra && (
                  <p><strong>Extra:</strong><br /><span className="opacity-90">{a.extra}</span></p>
                )}
                <div className="flex gap-3">
                  <a
                    href={`mailto:${a.email}?subject=The Sesh — your application`}
                    className="font-display text-base px-4 py-2 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] inline-flex items-center gap-2"
                  >
                    <Mail size={14} /> reply via email
                  </a>
                  <button
                    data-testid={`app-delete-${a.id}`}
                    onClick={(e) => remove(a.id, e)}
                    className="font-display text-base px-4 py-2 border-2 border-[var(--tsws-cigar)] text-[var(--tsws-cigar)] hover:bg-[var(--tsws-cigar)] hover:text-[var(--tsws-paper)] inline-flex items-center gap-2"
                  >
                    <Trash2 size={14} /> delete
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ===== Voicemails =====
function VoicemailsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [audioUrls, setAudioUrls] = useState({});

  const load = () =>
    axios
      .get(`${API}/admin/voicemails`, authHeaders(token))
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Couldn't load voicemails"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    return () => {
      // revoke any object URLs
      Object.values(audioUrls).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAudio = async (id) => {
    if (audioUrls[id]) return audioUrls[id];
    const r = await axios.get(`${API}/admin/voicemails/${id}/audio`, {
      ...authHeaders(token),
      responseType: "blob",
    });
    const url = URL.createObjectURL(r.data);
    setAudioUrls((m) => ({ ...m, [id]: url }));
    return url;
  };

  const toggleFeatured = async (id, current) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/voicemails/${id}`,
        { featured: !current },
        authHeaders(token)
      );
      setItems((prev) => prev.map((v) => (v.id === id ? { ...v, featured: data.featured } : v)));
      toast.success(data.featured ? "Marked as aired." : "Unmarked.");
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this voicemail forever?")) return;
    try {
      await axios.delete(`${API}/admin/voicemails/${id}`, authHeaders(token));
      setItems((prev) => prev.filter((v) => v.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div data-testid="voicemails-panel">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <span className="font-label text-xs opacity-70">{items.length} voicemails</span>
      </div>

      {loading && <p className="opacity-60">Loading...</p>}
      {!loading && items.length === 0 && (
        <div className="border-2 border-dashed border-[var(--tsws-green)] p-10 text-center">
          <Mic className="mx-auto text-[var(--tsws-cigar)]" size={32} />
          <p className="font-display text-2xl mt-3">no voicemails yet.</p>
        </div>
      )}

      <ul className="space-y-3" data-testid="voicemails-list">
        {items.map((v) => (
          <li
            key={v.id}
            data-testid={`voicemail-${v.id}`}
            className={`border-2 ${
              v.featured ? "border-[var(--tsws-cigar)] bg-[var(--tsws-cream)]" : "border-[var(--tsws-green)] bg-[var(--tsws-paper)]"
            } p-5`}
          >
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
              <span className="font-display text-2xl">
                {(v.from_name || "anonymous").toLowerCase()}
              </span>
              {v.from_email && (
                <a
                  href={`mailto:${v.from_email}`}
                  className="font-tag text-base opacity-80 hover:text-[var(--tsws-cigar)]"
                >
                  {v.from_email}
                </a>
              )}
              {v.duration_sec && (
                <span className="text-sm opacity-70">{Math.round(v.duration_sec)}s</span>
              )}
              {v.featured && (
                <span className="text-xs font-display px-2 py-0.5 bg-[var(--tsws-cigar)] text-[var(--tsws-paper)]">
                  AIRED
                </span>
              )}
              <span className="text-xs opacity-50 ml-auto">
                {new Date(v.submitted_at).toLocaleString()}
              </span>
            </div>

            {v.message && (
              <p className="mb-3 italic opacity-85">"{v.message}"</p>
            )}

            <VoicemailPlayer
              id={v.id}
              loadAudio={loadAudio}
              audioUrl={audioUrls[v.id]}
              isPlaying={playing === v.id}
              setPlaying={setPlaying}
            />

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                data-testid={`vm-feature-${v.id}`}
                onClick={() => toggleFeatured(v.id, v.featured)}
                className={`font-display text-sm px-4 py-2 border-2 border-[var(--tsws-green)] inline-flex items-center gap-2 ${
                  v.featured
                    ? "bg-[var(--tsws-cigar)] text-[var(--tsws-paper)] border-[var(--tsws-cigar)]"
                    : "hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
                }`}
              >
                {v.featured ? <><StarOff size={14} /> unmark</> : <><Star size={14} /> mark as aired</>}
              </button>
              <button
                data-testid={`vm-delete-${v.id}`}
                onClick={() => remove(v.id)}
                className="font-display text-sm px-4 py-2 border-2 border-[var(--tsws-cigar)] text-[var(--tsws-cigar)] hover:bg-[var(--tsws-cigar)] hover:text-[var(--tsws-paper)] inline-flex items-center gap-2"
              >
                <Trash2 size={14} /> delete
              </button>
              <span className="text-xs opacity-60 self-center ml-auto">
                email: {v.email_status}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VoicemailPlayer({ id, loadAudio, audioUrl, isPlaying, setPlaying }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!audioUrl) {
      setLoading(true);
      await loadAudio(id);
      setLoading(false);
    }
    setPlaying(isPlaying ? null : id);
  };

  return (
    <div className="flex items-center gap-3">
      {!audioUrl ? (
        <button
          data-testid={`vm-load-${id}`}
          onClick={handleClick}
          disabled={loading}
          className="font-display text-sm px-4 py-2 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Play size={14} /> {loading ? "loading..." : "play"}
        </button>
      ) : (
        <audio
          data-testid={`vm-audio-${id}`}
          src={audioUrl}
          controls
          autoPlay={isPlaying}
          className="w-full max-w-md"
        />
      )}
    </div>
  );
}

// ===== Suggestions =====
function SuggestionsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () =>
    axios
      .get(`${API}/admin/suggestions`, authHeaders(token))
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Couldn't load suggestions"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, [token]);

  const toggleSpam = async (id, current) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/suggestions/${id}`,
        { spam: !current },
        authHeaders(token)
      );
      setItems((prev) => prev.map((s) => (s.id === id ? { ...s, spam: data.spam } : s)));
      toast.success(data.spam ? "Marked as spam." : "Unmarked.");
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this suggestion forever?")) return;
    try {
      await axios.delete(`${API}/admin/suggestions/${id}`, authHeaders(token));
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const filtered =
    filter === "all" ? items : filter === "spam" ? items.filter((i) => i.spam) : items.filter((i) => !i.spam);

  return (
    <div data-testid="suggestions-panel">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          { id: "all", label: "all" },
          { id: "live", label: "live" },
          { id: "spam", label: "spam" },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`sug-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`font-display text-sm px-4 py-2 border-2 border-[var(--tsws-green)] ${
              filter === f.id ? "bg-[var(--tsws-green)] text-[var(--tsws-orange)]" : "hover:bg-[var(--tsws-green)]/10"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="font-label text-xs opacity-70 ml-auto">{filtered.length} of {items.length}</span>
      </div>

      {loading && <p className="opacity-60">Loading...</p>}
      {!loading && filtered.length === 0 && (
        <div className="border-2 border-dashed border-[var(--tsws-green)] p-10 text-center">
          <Lightbulb className="mx-auto text-[var(--tsws-cigar)]" size={32} />
          <p className="font-display text-2xl mt-3">nothing here.</p>
        </div>
      )}

      <ul className="space-y-3" data-testid="suggestions-list">
        {filtered.map((s) => (
          <li
            key={s.id}
            data-testid={`suggestion-${s.id}`}
            className={`border-2 ${
              s.spam ? "border-[var(--tsws-cigar)] opacity-70" : "border-[var(--tsws-green)]"
            } bg-[var(--tsws-paper)] p-5 flex gap-4 items-start`}
          >
            <div className="shrink-0 w-14 flex flex-col items-center justify-center py-2 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)]">
              <span className="font-display text-xl leading-none">{s.upvotes || 0}</span>
              <span className="text-[10px] opacity-80">votes</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-display text-[10px] px-2 py-0.5 bg-[var(--tsws-green)] text-[var(--tsws-orange)] uppercase">
                  {s.category}
                </span>
                {s.spam && (
                  <span className="font-display text-[10px] px-2 py-0.5 bg-[var(--tsws-cigar)] text-[var(--tsws-paper)] uppercase">
                    spam
                  </span>
                )}
                <span className="text-xs opacity-50 ml-auto">
                  {new Date(s.created_at).toLocaleDateString()}
                </span>
              </div>
              <h3 className="font-display text-xl lg:text-2xl leading-tight">
                {s.title.toLowerCase()}
              </h3>
              {s.body && <p className="mt-2 opacity-90">{s.body}</p>}
              <p className="mt-2 font-tag text-sm opacity-70">— {s.name?.trim() || "anonymous"}</p>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  data-testid={`sug-spam-${s.id}`}
                  onClick={() => toggleSpam(s.id, s.spam)}
                  className="font-display text-sm px-3 py-1.5 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
                >
                  {s.spam ? "unmark spam" : "mark spam"}
                </button>
                <button
                  data-testid={`sug-delete-${s.id}`}
                  onClick={() => remove(s.id)}
                  className="font-display text-sm px-3 py-1.5 border-2 border-[var(--tsws-cigar)] text-[var(--tsws-cigar)] hover:bg-[var(--tsws-cigar)] hover:text-[var(--tsws-paper)]"
                >
                  delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
